import { Navigate, RouterProvider, createBrowserRouter } from "react-router-dom";
import { lazy, Suspense } from "react";
import { useAuth } from "../provider/authProvider";
import { AccessProtectedRoute, ProtectedRoute } from "./ProtectedRoute";
import Layout from '../Components/Layout';

// Loading Fallback Component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin"></div>
      <p className="text-gray-600 font-medium">Loading...</p>
    </div>
  </div>
);

// Lazy loaded components
const CommonSignupFlow = lazy(() => import('../Components/Signup/CommonSignupFlow'));
const Login = lazy(() => import('../Components/Login/Login'));
const ForgotPassword = lazy(() => import('../Components/Login/ForgotPassword'));
const Dashboard = lazy(() => import('../Components/Dashboard/Dashboard'));
const DashboardHome = lazy(() => import('../Components/Dashboard/Home.jsx/Home'));
const Booking = lazy(() => import('../Components/Dashboard/Bookings/Booking'));
const Priority = lazy(() => import('../Components/Dashboard/PriorityDM/Priority'));
const Payments = lazy(() => import('../Components/Dashboard/Payments/Payments'));
const PageNotFound = lazy(() => import("../Pages/PageNotFound/PageNotFound"));
const PrivacyAndPolicy = lazy(() => import("../Pages/PrivacyAndPolicy/PrivacyAndPolicy"));
const RefundPolicy = lazy(() => import("../Pages/RefundPolicy/RefundPolicy"));
const MentorDetails = lazy(() => import("../Components/Mentor/MentorDetails/MentorDetails"));
const Contact = lazy(() => import("../Components/Contact/Contact"));
const MentorOnboarding = lazy(() => import("../Components/Signup/MentorOnboarding/MentorOnboarding"));
const CreateNewPassword = lazy(() => import("../Components/Login/CreateNewPassword"));
const Sessions = lazy(() => import("../Components/Dashboard/Services/Sessions"));
const MentorsList = lazy(() => import("../Components/Mentor/MentorsList/MentorsList"));
const Careers = lazy(() => import("../Components/Careers/Careers"));
const Account = lazy(() => import("../Components/Dashboard/Account/Account"));
const PreviewBookingDetails = lazy(() => import("../Components/Mentor/PreviewBookingDetails/PreviewBookingDetails"));
const Rewards = lazy(() => import("../Components/Dashboard/Rewards/Rewards"));
const Home = lazy(() => import("../LandingPages/Home/Home"));
const JobList = lazy(() => import("../Components/Careers/JobList/JobList"));
const JobDetail = lazy(() => import("../Components/Careers/JobList/Jobdetail"));
const JobForm = lazy(() => import("../Components/Careers/JobList/JobForm"));
const About = lazy(() => import("../Components/About/About"));
const Room = lazy(() => import("../Components/Dashboard/Bookings/MeetingRoom"));
const ReviewForm = lazy(() => import("../Components/Dashboard/Bookings/ReviewForm"));
const LoginRedirectWrapper = lazy(() => import('./ProtectedRoute'));
const BhopalFirstEvent = lazy(() => import("../Pages/CodeQueueEvents/BhopalFirstEvent"));
const BhopalEventForm = lazy(() => import("../Pages/CodeQueueEvents/BhopalEventForm"));
const Contest = lazy(() => import("../Pages/CodeQueueEvents/Contest"));
const OnlineCompiler = lazy(() => import("../Components/OnlinePractice/OnlineCompiler/OnlineCompiler"));
const ReferralRequest = lazy(() => import("../Components/Mentor/MentorPricing/ReferralRequest"));
const PriorityDM = lazy(() => import("../Components/Mentor/MentorPricing/PriorityDM"));
const ChooseUserType = lazy(() => import("../Components/OnlinePractice/CodingContest/ChooseUserType"));
const Referral = lazy(() => import("../Components/Dashboard/Referral/Referral"));
const MentorProtectedRoute = lazy(() => import("./MentorProtectedRoute"));
const Ltm = lazy(() => import("../Components/Dashboard/LTM/Ltm"));
const RegisterPlan = lazy(() => import("../Components/Dashboard/LTM/NewRegistration/RegisterPlan"));
const Tickets = lazy(() => import("../Components/Dashboard/LTM/Tickets/Tickets"));
const LtmMeetingRoom = lazy(() => import("../Components/Dashboard/LTM/Meetings/LtmMeetingRoom"));
const LtmReviewForMentor = lazy(() => import("../Components/Dashboard/LTM/Meetings/LtmReviewForMentor"));
const Ambassador = lazy(() => import("../Components/CampusAmbassador/Ambassador"));
const ApplyForAmbassador = lazy(() => import("../Components/CampusAmbassador/ApplyForAmbassador"));
const CandidateAction = lazy(() => import("../Components/CampusAmbassador/CandidateAction"));
const Products = lazy(() => import("../Components/Products/Products"));
const Quizzes = lazy(()=> import("../Components/Quizzes/Quizzes"))
const Quiz = lazy(()=> import("../Components/Quizzes/Quiz"))
const Common = lazy(()=> import("../Components/Common"))
const Organizations = lazy(()=> import("../Components/Organizations/Organizations"))
const OrgLayout = lazy(() => import("../Components/Organizations/OrgLayout"));
const OrganizationPricing = lazy(() => import("../Components/Organizations/pricing/Pricing"));
const OrganizationRequestDemo = lazy(() => import("../Components/Organizations/requestDemo/RequestDemo"));


const Routes = () => {
    const { token } = useAuth();

    const routesForPublic = [
        {
            path: "/",
            element: (
                <Suspense fallback={<LoadingFallback />}>
                <LoginRedirectWrapper>
                    <Suspense fallback={<LoadingFallback />}>
                        <Common />
                    </Suspense>
                </LoginRedirectWrapper>
                </Suspense>
            ),
            // element: <Layout><Suspense fallback={<LoadingFallback />}><Home /></Suspense></Layout>,
        },

        {
            path: "*",
            element: <Layout><Suspense fallback={<LoadingFallback />}><PageNotFound /></Suspense></Layout>
        },
        {
            path: "/privacy-and-policy",
            element: <Layout><Suspense fallback={<LoadingFallback />}><PrivacyAndPolicy /></Suspense></Layout>
        },
        {
            path: "/refund-policy",
            element: <Layout><Suspense fallback={<LoadingFallback />}><RefundPolicy /></Suspense></Layout>
        },
        {
            path: "/jobs",
            element: <Layout><Suspense fallback={<LoadingFallback />}><JobList /></Suspense></Layout>
        },
        {
            path: "/jobs/:jobId",
            element: <Layout><Suspense fallback={<LoadingFallback />}><JobDetail /></Suspense></Layout>,
        },
        {
            path: "/jobs/:jobId/apply",
            element: <Layout><Suspense fallback={<LoadingFallback />}><JobForm /></Suspense></Layout>,
        },
        {
            path: "/mentors-list",
            element: <Layout><Suspense fallback={<LoadingFallback />}><MentorsList /></Suspense></Layout>,
            children: [
                {
                    path: "details",
                    element: <Suspense fallback={<LoadingFallback />}><MentorDetails /></Suspense>
                }
            ]
        },
        {
            path: "/contact",
            element: <Layout><Suspense fallback={<LoadingFallback />}><Contact /></Suspense></Layout>
        },
        {
            path: "/careers",
            element: <Layout><Suspense fallback={<LoadingFallback />}><Careers /></Suspense></Layout>
        },
        {
            path: "/about",
            element: <Layout><Suspense fallback={<LoadingFallback />}><About /></Suspense></Layout>
        },
        {
            path: "/onboarding",
            element: <Layout><Suspense fallback={<LoadingFallback />}><MentorOnboarding /></Suspense></Layout>
        },
        {
            path: "/room/",
            element: <Suspense fallback={<LoadingFallback />}><Room /></Suspense>,
        },
        {
            path: "/room/review/",
            element: <Suspense fallback={<LoadingFallback />}><ReviewForm /></Suspense>
        },
        {
            path: "/ltm-room/",
            element: <Suspense fallback={<LoadingFallback />}><LtmMeetingRoom /></Suspense>,
        },
        {
            path: "/ltm-room/ltm-review/:id",
            element: <Suspense fallback={<LoadingFallback />}><LtmReviewForMentor /></Suspense>,
        },
        {
            path: "/login",
            element: (
                <Suspense fallback={<LoadingFallback />}>
                    <LoginRedirectWrapper>
                        <Layout><Suspense fallback={<LoadingFallback />}><Login /></Suspense></Layout>
                    </LoginRedirectWrapper>
                </Suspense>
            ),
        },
        {
            path: "/signup",
            element: (
                <Suspense fallback={<LoadingFallback />}>
                <LoginRedirectWrapper>
                    <Layout>
                    <Suspense fallback={<LoadingFallback />}>
                        <CommonSignupFlow />
                    </Suspense>
                    </Layout>
                </LoginRedirectWrapper>
                </Suspense>
            ),
        },
        {
            path: "/forgot-password",
            element: (
                <Suspense fallback={<LoadingFallback />}>
                    <LoginRedirectWrapper>
                        <Layout><Suspense fallback={<LoadingFallback />}><ForgotPassword /></Suspense></Layout>
                    </LoginRedirectWrapper>
                </Suspense>
            ),
        },
        {
            path: "/new-password",
            element: (
                <Suspense fallback={<LoadingFallback />}>
                    <LoginRedirectWrapper>
                        <Layout><Suspense fallback={<LoadingFallback />}><CreateNewPassword /></Suspense></Layout>
                    </LoginRedirectWrapper>
                </Suspense>
            ),
        },
        {
            path: "/online-compiler",
            element: <Layout><Suspense fallback={<LoadingFallback />}><OnlineCompiler /></Suspense></Layout>
        },
        {
            path: "/coding-contest",
            element: <Layout><Suspense fallback={<LoadingFallback />}><ChooseUserType /></Suspense></Layout>
        },
        {
            path : "referral-request",
            element: <Suspense fallback={<LoadingFallback />}><ReferralRequest /></Suspense>
        },
        {
            path : "priority-dm",
            element: <Suspense fallback={<LoadingFallback />}><PriorityDM /></Suspense>
        },
        {
            path : "ambassador",
            element: <Layout><Suspense fallback={<LoadingFallback />}><Ambassador /></Suspense></Layout>
        },
        {
            path: "campus-ambassador/action",
            element: <Suspense fallback={<LoadingFallback />}><CandidateAction /></Suspense>
        },
        {            
            path : "ambassador/apply",
            element: <Layout><Suspense fallback={<LoadingFallback />}><ApplyForAmbassador /></Suspense></Layout>
        },
        {
            path : "products",
            element: <Layout><Suspense fallback={<LoadingFallback />}><Products /></Suspense></Layout>
        },
        {
            path : "individual",
            element: <Layout><Suspense fallback={<LoadingFallback />}><Home /></Suspense></Layout>
        },
        {
            path: "/organizations",
            element: (
                <Suspense fallback={<LoadingFallback />}>
                    <LoginRedirectWrapper>
                        <OrgLayout />
                    </LoginRedirectWrapper>
                </Suspense>
            ),
            children: [
                {
                    index: true,
                    element: <Suspense fallback={<LoadingFallback />}><Organizations /></Suspense>,
                },
                {
                    path: "pricing",
                    element: <Suspense fallback={<LoadingFallback />}><OrganizationPricing /></Suspense>,
                },
                {
                    path: "request-demo",
                    element: <Suspense fallback={<LoadingFallback />}><OrganizationRequestDemo /></Suspense>,
                },
            ],
        },
    ];

    const routesForAuthenticatedOnly = [
        {
            path: "/",
            element: <ProtectedRoute />,
            children: [
                {
                    path: "/dashboard",
                    element: <Layout><Suspense fallback={<LoadingFallback />}><Dashboard /></Suspense></Layout>,
                    children: [
                        {
                            index: true,
                            element: <Navigate replace to="home" />,
                        },
                        {
                            path: "home",
                            element: <Suspense fallback={<LoadingFallback />}><DashboardHome /></Suspense>,
                        },
                        {
                            path: "bookings",
                            element: <Suspense fallback={<LoadingFallback />}><Booking /></Suspense>,
                        },
                        {
                            path: "sessions",
                            element: <Suspense fallback={<LoadingFallback />}><MentorProtectedRoute /></Suspense>,
                            children: [
                                { index: true, element: <Suspense fallback={<LoadingFallback />}><Sessions /></Suspense> }
                            ]
                        },
                        {
                            path: "prioritydm",
                            element: <Suspense fallback={<LoadingFallback />}><MentorProtectedRoute /></Suspense>,
                            children: [
                                { index: true, element: <Suspense fallback={<LoadingFallback />}><Priority /></Suspense> }
                            ]
                        },
                        {
                            path: "referral",
                            element: <Suspense fallback={<LoadingFallback />}><MentorProtectedRoute /></Suspense>,
                            children: [
                                { index: true, element: <Suspense fallback={<LoadingFallback />}><Referral /></Suspense> }
                            ]
                        },
                        {
                            path: "payments",
                            element: <Suspense fallback={<LoadingFallback />}><MentorProtectedRoute /></Suspense>,
                            children: [
                                { index: true, element: <Suspense fallback={<LoadingFallback />}><Payments /></Suspense> }
                            ]
                        },
                        {
                            path: "account",
                            element: <Suspense fallback={<LoadingFallback />}><Account /></Suspense>
                        },
                        {
                            path: "rewards",
                            element: <Suspense fallback={<LoadingFallback />}><Rewards /></Suspense>
                        },
                        {
                            path: "contest",
                            element: <Suspense fallback={<LoadingFallback />}><Contest /></Suspense>
                        },
                        {
                            path: "ltm",
                            element: <Suspense fallback={<LoadingFallback />}><AccessProtectedRoute /></Suspense>,
                            children: [
                                { index: true, element: <Suspense fallback={<LoadingFallback />}><Ltm /></Suspense> },
                                { path: "tickets", element: <Suspense fallback={<LoadingFallback />}><Tickets /></Suspense> }
                            ]
                        },
                    ],
                },
                {
                    path: "preview-bookings",
                    element: <Layout><Suspense fallback={<LoadingFallback />}><PreviewBookingDetails /></Suspense></Layout>
                },
                {
                    path : "/codequeue-contest/form",
                    element : <Layout><Suspense fallback={<LoadingFallback />}><BhopalEventForm /></Suspense></Layout>
                },
                {
                    path : "/register-plan",
                    children: [
                        { 
                            index: true, 
                            path: "org",
                            element : <Layout><Suspense fallback={<LoadingFallback />}><RegisterPlan /></Suspense></Layout>,
                        }
                    ]
                },
                {
                    path: "/quizzes",
                    children: [
                        {
                            index: true,
                            element: <Layout><Suspense fallback={<LoadingFallback />}><Quizzes /></Suspense></Layout>
                        },
                        {
                            path: ":id",
                            element: <Layout><Suspense fallback={<LoadingFallback />}><Quiz /></Suspense></Layout>
                        }
                    ]
                }
            ],
        }
    ];

    const routesForNotAuthenticatedOnly = [
        {
            path: "/login",
            element: <Layout><Suspense fallback={<LoadingFallback />}><Login /></Suspense></Layout>,
        },
        {
            path: "/signup",
            element: <Layout><Suspense fallback={<LoadingFallback />}><CommonSignupFlow /></Suspense></Layout>,
        },
        {
            path: "/forgot-password",
            element: <Layout><Suspense fallback={<LoadingFallback />}><ForgotPassword /></Suspense></Layout>
        },
        {
            path: "/new-password",
            element: <Layout><Suspense fallback={<LoadingFallback />}><CreateNewPassword /></Suspense></Layout>
        }
    ];

    const router = createBrowserRouter([
        ...routesForPublic,
        ...routesForAuthenticatedOnly,
        ...(!token ? routesForNotAuthenticatedOnly : []),

    ]);

    return <RouterProvider router={router} />;
};

export default Routes;
